import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { ErrorMessage } from "components/ui/error-message";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { useProductName } from "store/selectors/branding";
import { useCurrentTenant } from "store/selectors/hooks";
import { OutgoingTransferCard } from "./outgoing-transfer-card";
import { SelectAgentForClientTransfer } from "./select-agent-for-client-transfer";
import { ClientTransferSubject, TransferSearchLocation } from "./transfer-types";

interface TransferClientPanelProps {
    subject: ClientTransferSubject;
    displayName: string;
    location: TransferSearchLocation | null;
    onDismiss: () => void;
}

export const TransferClientPanel: React.FC<TransferClientPanelProps> = props => {
    const { subject, displayName, location, onDismiss } = props;
    const tenant = useCurrentTenant();
    const productName = useProductName();
    const trpcContext = trpc.useTRPC();

    const query = useQuery(
        trpcContext.clientTransfer.findBySubject.queryOptions({ subject }),
    );

    const refetch = useCallback(async () => {
        await query.refetch();
    }, [query]);

    const renderBody = () => {
        if (query.isLoading) {
            return <Spinner />;
        }

        if (query.isError) {
            return (
                <ErrorMessage component="Transfer client">
                    Unable to load transfer status.
                </ErrorMessage>
            );
        }

        if (query.data?.status === "Pending") {
            return (
                <>
                    <p>
                        There is a transfer in progress to {query.data.sentToName}, an
                        advisor outside of {tenant.name}.
                    </p>
                    <OutgoingTransferCard
                        clientTransferRequest={query.data}
                        onTransferChanged={refetch}
                    />
                </>
            );
        }

        if (query.data?.status === "Accepted") {
            return (
                <p>
                    This client has been transferred to {query.data.sentToName}, an
                    advisor outside of {tenant.name}.
                </p>
            );
        }

        return (
            <>
                <p>Request to transfer this client to another {productName} customer.</p>
                <SelectAgentForClientTransfer
                    subject={subject}
                    location={location}
                    onTransferred={refetch}
                />
            </>
        );
    };

    return (
        <Panel
            type={PanelType.large}
            headerText={`Transfer client ${displayName}`}
            onDismiss={onDismiss}
            isOpen={true}>
            <div className="space-y-4">{renderBody()}</div>
        </Panel>
    );
};
